import { HubService, HubServiceEvents, type IHubService } from './HubService';
import { LoggerServiceFactory } from './LoggerService';

export const LOGIN_APP_PENDING = '/login/app';
export const LOGIN_APP_EXPIRED = '/app/complete';

export type AppHubUser = {
  type: 'retail' | 'corporate';
  interactionId: string;
  name: string;
  sessionId: string;
  userName: string;
};

export type AppHubQrCode = {
  image: string;
  url?: string;
  pointer: string;
  debugUrl?: string;
};

export enum AppHubStateType {
  Empty = 'empty',
  Suspended = 'suspended',
  Login = 'login',
  Pending = 'pending',
  Completed = 'completed',
  Error = 'error',
}

type AppHubStateEmpty = { type: AppHubStateType.Empty; debugUrl?: string };
type AppHubStateSuspended = { type: AppHubStateType.Suspended; url?: string };
type AppHubStateLogin = {
  type: AppHubStateType.Login;
  qr?: AppHubQrCode;
  users?: AppHubUser[];
};
type AppHubStatePending = {
  type: AppHubStateType.Pending;
  url: string;
  debugUrl?: string;
};
type AppHubStateCompleted = { type: AppHubStateType.Completed; url: string };
type AppHubStateError = {
  type: AppHubStateType.Error;
  error: 'access_denied' | string;
  description: string;
};

type AppHubResponseReceiveStartedAsync = { id: string; debugUrl?: string };
type AppHubResponseReceiveQrAsync = {
  pointer: string;
  data: string;
  url: string;
  debugUrl?: string;
};

export type AppHubState =
  | AppHubStateEmpty
  | AppHubStateSuspended
  | AppHubStateLogin
  | AppHubStatePending
  | AppHubStateCompleted
  | AppHubStateError;

export interface IAppHubServiceListener {
  onStateChanged(callback: (data: AppHubState) => void): void;
}

export interface IAppHubService extends IHubService, IAppHubServiceListener {
  state: AppHubState;
  setPersistent(isPersistent: boolean): void;
  fetchQrCode(): Promise<void>;
  authenticateUser(user: AppHubUser): Promise<void>;
  revokeUser(user: AppHubUser): Promise<boolean>;
}

export type AppHubStateListener = (state: AppHubState) => void;

export enum AppHubServiceEvents {
  ReceiveAcceptedAsync = 'ReceiveAcceptedAsync',
  ReceiveUsersAsync = 'ReceiveUsersAsync',
  ReceiveQrAsync = 'ReceiveQrAsync',
  ReceiveExpiredAsync = 'ReceiveExpiredAsync',
  ReceiveQrExpiredAsync = 'ReceiveQrExpiredAsync',
  OnStateChanged = 'OnStateChanged',
}

export class AppHubService extends HubService implements IAppHubService {
  public state: AppHubState = { type: AppHubStateType.Empty };
  private isPersistent: boolean = false;
  private users: AppHubUser[] = [];
  private qr: AppHubQrCode | undefined;
  constructor() {
    super({
      path: '/hubs/app',
      logger: LoggerServiceFactory.create('AppHubService'),
    });

    this.on(
      HubServiceEvents.ReceiveStartedAsync,
      (data: AppHubResponseReceiveStartedAsync) => {
        this.setState({
          type: AppHubStateType.Empty,
          debugUrl: data.debugUrl,
        });
      },
    );

    this.on(HubServiceEvents.ReceiveCompletedAsync, (url: string) => {
      this.setState({ type: AppHubStateType.Completed, url });
    });

    this.on(
      HubServiceEvents.ReceiveErrorAsync,
      (error: string, description: string) => {
        this.setState({
          type: AppHubStateType.Error,
          error,
          description,
        });
      },
    );

    this.on(
      AppHubServiceEvents.ReceiveAcceptedAsync,
      async (url: string, token: string) => {
        this.logger.trace('Event ReceiveAcceptedAsync', { data: { url } });
        await this.onHubAccepted(url, token);
      },
    );

    this.on(AppHubServiceEvents.ReceiveUsersAsync, (users: AppHubUser[]) => {
      this.logger.trace('Event ReceiveUsersAsync', { data: { users } });
      this.setState({
        type: AppHubStateType.Login,
        users: users,
        qr: this.qr,
      });
    });

    this.on(
      AppHubServiceEvents.ReceiveQrAsync,
      ({
        pointer,
        data: image,
        url,
        debugUrl,
      }: AppHubResponseReceiveQrAsync) => {
        this.setState({
          type: AppHubStateType.Login,
          qr: {
            image,
            url,
            pointer,
            debugUrl,
          },
          users: this.users,
        });
      },
    );

    this.on(AppHubServiceEvents.ReceiveExpiredAsync, (url?: string) => {
      this.logger.trace('Event ReceiveExpiredAsync', { data: { url } });
      this.setState({ type: AppHubStateType.Suspended, url });
    });

    this.on(AppHubServiceEvents.ReceiveQrExpiredAsync, (url?: string) => {
      this.logger.trace('Event ReceiveQrExpiredAsync', { data: { url } });
      this.setState({ type: AppHubStateType.Suspended, url });
    });
  }

  private async setState(data: AppHubState) {
    this.state = data;
    this.broadcast(AppHubServiceEvents.OnStateChanged, data);
    switch (data.type) {
      case AppHubStateType.Login:
        // local cache of current users and qr code data
        this.users = data.users || [];
        this.qr = data.qr;
        break;
      case AppHubStateType.Suspended:
      case AppHubStateType.Completed:
      case AppHubStateType.Error:
        await this.stop();
        break;
    }
  }

  private async onHubAccepted(url: string, token: string) {
    const body = {
      token: token,
      persistent: this.isPersistent,
    };
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    };

    const request = new Request(url, options);
    const response: Response = await fetch(request);
    if (response.ok) {
      try {
        const responseJson = (await response.json()) as {
          data: { nextStep: string; debugUrl?: string };
        };
        this.logger.trace(
          'Received next step instructions for pending authentication',
          { data: { response: responseJson } },
        );
        this.setState({
          type: AppHubStateType.Pending,
          url: responseJson.data.nextStep,
          debugUrl: responseJson.data.debugUrl,
        });
      } catch (error) {
        this.setState({
          type: AppHubStateType.Error,
          error: 'Something errorred...',
          description: '',
        });
      }
    } else {
      this.setState({
        type: AppHubStateType.Error,
        error: 'Something errorred...',
        description: '',
      });
    }
  }

  public onStateChanged(callback: (data: AppHubState) => void): void {
    this.on(AppHubServiceEvents.OnStateChanged, callback);
  }

  public setPersistent(isPersistent: boolean): void {
    this.isPersistent = isPersistent;
  }

  public async fetchQrCode() {
    return super.send('FetchQrAsync');
  }

  public async authenticateUser(user: AppHubUser) {
    // optimistically set state to "pending"
    this.setState({ type: AppHubStateType.Pending, url: LOGIN_APP_PENDING });

    const body = {
      userName: user.userName,
      sessionId: user.sessionId,
      interactionId: user.interactionId,
    };
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    };

    const request = new Request('/ui/login/app', options);
    const response: Response = await fetch(request);
    if (response.ok) {
      try {
        const responseJson = (await response.json()) as {
          data: { nextStep: string; debugUrl?: string };
        };
        this.logger.trace('authenticateUser', {
          data: { response: responseJson },
        });
        this.setState({
          type: AppHubStateType.Pending,
          url: responseJson.data.nextStep,
          debugUrl: responseJson.data.debugUrl,
        });
      } catch (error) {
        this.setState({
          type: AppHubStateType.Error,
          error: 'Something errorred...',
          description: '',
        });
      }
    } else {
      this.setState({
        type: AppHubStateType.Error,
        error: 'Something errorred...',
        description: '',
      });
    }
  }

  public async revokeUser(user: AppHubUser) {
    const body = {
      userName: user.userName,
      sessionId: user.sessionId,
    };
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    };

    const request = new Request('/app/revoke', options);
    const response: Response = await fetch(request);
    if (response.ok) {
      this.logger.trace('User revoked successfully');
      if (this.state.type === AppHubStateType.Login) {
        // Filter out the user that was revoked
        const filteredUsers = this.state.users?.filter(
          (x) => x.userName !== user.userName,
        );
        // Update the state with the new user list
        this.setState({
          type: AppHubStateType.Login,
          users: filteredUsers,
          qr: this.state.qr,
        });
        // If there are no users left, fetch a new qr code
        if (filteredUsers?.length === 0) {
          this.fetchQrCode();
        }
      }
      return true;
    } else {
      this.logger.trace('Failed to revoke user', {
        data: { response: `${response.status} ${response.statusText}` },
      });
      return false;
    }
  }

  public async start() {
    if (this.state.type === 'empty') {
      return super.start();
    }
  }
}

export const AppHubServiceToken = Symbol(AppHubService.name);
